// AlertManagement.jsx – API‑driven (no localStorage)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import cls from './AlertManagement.module.css';

const EMAIL_REGEX = /^(?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/;

// Edit these as needed
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api/alert`

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export default function AlertManagement() {
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [ccList, setCcList] = useState([]);
  const [delayValue, setDelayValue] = useState(7);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [delayUnit, setDelayUnit] = useState('days');
  const [resendDelayValue, setResendDelayValue] = useState(3); // Default resend delay
  const [resendDelayUnit, setResendDelayUnit] = useState('days'); // Default resend delay unit

  // ─────────────────── fetch settings ───────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(API_URL);
        setTemplate(data.emailMessage ?? '');
        setCcList(data.ccEmails ?? ['ops@example.com']);
        setDelayValue(data.notificationDelay ?? 7);
        setDelayUnit(data.notificationDelayUnit ?? 'days');

        // Handle resendDelay from backend (milliseconds)
        if (data.resendDelay && typeof data.resendDelay === 'number') {
          const msFromBackend = data.resendDelay;
          if (msFromBackend % MS_PER_DAY === 0) {
            const days = msFromBackend / MS_PER_DAY;
            if (days >= 1) {
              setResendDelayValue(days);
              setResendDelayUnit('days');
            }
          } else if (msFromBackend % MS_PER_HOUR === 0) {
            const hours = msFromBackend / MS_PER_HOUR;
            if (hours >= 1) {
              setResendDelayValue(hours);
              setResendDelayUnit('hours');
            }
          }
          // If not cleanly convertible or <1 unit, useState defaults (3 days) will remain
        }
      } catch (err) {
        console.error('Failed to load alert settings', err);
        toast.error('Unable to load settings, using defaults');
        setTemplate('');
        setCcList(['ops@example.com']);
        // useState defaults for delayValue, delayUnit, resendDelayValue, resendDelayUnit will apply
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const invalidEmails = useMemo(() => ccList.filter((e) => !EMAIL_REGEX.test(e)), [ccList]);

  const addEmail = useCallback(() => {
    const val = ccInput.trim();
    if (!val) return;
    if (!EMAIL_REGEX.test(val)) return toast.error('Invalid email');
    if (ccList.includes(val)) return toast.info('Email already added');
    setCcList((prev) => [...prev, val]);
    setCcInput('');
  }, [ccInput, ccList]);

  const removeEmail = (email) => setCcList((prev) => prev.filter((e) => e !== email));
  const handleKeyDown = (e) => {
    if (['Enter', ','].includes(e.key)) {
      e.preventDefault();
      addEmail();
    }
  };

  // ─────────────────── save ───────────────────
  const save = async (e) => {
    e.preventDefault();
    if (invalidEmails.length) return toast.error('Fix invalid emails before saving');

    let resendDelayInMs;
    const numericResendValue = Number(resendDelayValue);
    if (resendDelayUnit === 'days') {
      resendDelayInMs = numericResendValue * MS_PER_DAY;
    } else { // 'hours'
      resendDelayInMs = numericResendValue * MS_PER_HOUR;
    }

    const payload = {
      emailMessage: template,
      ccEmails: ccList,
      notificationDelay: Number(delayValue),
      notificationDelayUnit: delayUnit,
      resendDelay: resendDelayInMs, // Send as milliseconds
    };

    try {
      await axios.put(API_URL, payload); // or POST depending on backend
      toast.success('Alert settings saved');
    } catch (err) {
      console.error('Save failed', err);
      toast.error('Failed to save settings');
    }
  };

  // ─────────────────── send test email ───────────────────
  const handleSendTestEmail = async () => {
    const toEmail = prompt('Enter the email address to send the test email to:');
    if (!toEmail) {
      return; // User cancelled or entered nothing
    }
    if (!EMAIL_REGEX.test(toEmail)) {
      return toast.error('Invalid recipient email address.');
    }

    setIsSendingTest(true);
    try {
      // Replace placeholders with sample data
      let testBody = template;
      const now = new Date();
      const dueDate = new Date(now);
      dueDate.setDate(now.getDate() - (delayValue || 7)); // Simulate an overdue order


      testBody = testBody.replace(/\[UserName\]/g, 'Test User');
      testBody = testBody.replace(/\[OrderNumber\]/g, 'ORDER-TEST-123');
      testBody = testBody.replace(/\[OrderName\]/g, 'Test Order');
      testBody = testBody.replace(/\[DueDate\]/g, dueDate.toLocaleDateString());

      const payload = {
        toEmail,
        body: testBody,
        ccEmails: ccList,
      };

      await axios.post(`${API_URL}/send-email`, payload); // Use the defined API_URL
      toast.success(`Test email sent to ${toEmail}`);
    } catch (err) {
      console.error('Failed to send test email', err);
      toast.error(err.response?.data?.message || 'Failed to send test email. Check console for details.');
    } finally {
      setIsSendingTest(false);
    }
  };
  if (loading) return <p className={cls.loading}>Loading alert settings…</p>;

  return (
    <div className={cls.container}>
      <h2 className={cls.title}>Alert Management</h2>
      <form className={cls.card} onSubmit={save}>
        {/* Template */}
        <section className={cls.section}>
          <h3 className={cls.sectionTitle}>Email Template</h3>
          <textarea
            className={cls.textarea}
            rows={8}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          />
          <span className={cls.hint}>
            Placeholders: [UserName] [OrderNumber] [OrderName] [DueDate]
          </span>
        </section>

        {/* CC Recipients */}
        <section className={cls.section}>
          <h3 className={cls.sectionTitle}>CC Recipients</h3>
          <div className={cls.ccInputRow}>
            <input
              className={cls.input}
              placeholder="Add email and press Enter"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              className={cls.addBtn}
              onClick={addEmail}
              disabled={!ccInput.trim()}
            >
              Add
            </button>
          </div>
          {!!ccList.length && (
            <div className={cls.chipList}>
              {ccList.map((email) => (
                <span key={email} className={cls.chip}>
                  {email}
                  <button type="button" className={cls.chipClose} onClick={() => removeEmail(email)}>
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Schedule */}
        <section className={cls.section}>
          <h3 className={cls.sectionTitle}>Schedule</h3>
          <div className={cls.scheduleRow}>
            <input
              type="number"
              min={1}
              className={cls.inputSmall}
              value={delayValue}
              onChange={(e) => setDelayValue(e.target.value)}
            />
            <select
              value={delayUnit}
              onChange={(e) => setDelayUnit(e.target.value)}
              className={cls.select}
            >
              <option value="days">days</option>
              <option value="hours">hours</option>
            </select>
            <span className={cls.hint}>after the due date</span>
          </div>
        </section>

        {/* Resend Schedule */}
        <section className={cls.section}>
          <h3 className={cls.sectionTitle}>Follow-up Notification Schedule</h3>
          <div className={cls.scheduleRow}>
            <input
              type="number"
              min={1}
              className={cls.inputSmall}
              value={resendDelayValue}
              onChange={(e) => setResendDelayValue(e.target.value)}
            />
            <select
              value={resendDelayUnit}
              onChange={(e) => setResendDelayUnit(e.target.value)}
              className={cls.select}
            >
              <option value="days">days</option>
              <option value="hours">hours</option>
            </select>
            <span className={cls.hint}>after the previous notification if order not returned</span>
          </div>
        </section>

        <div className={cls.actionsRow}>
          <button
            type="button"
            className={`${cls.saveBtn} ${cls.testBtn}`}
            onClick={handleSendTestEmail}
            disabled={isSendingTest || loading}
          >
            {isSendingTest ? 'Sending...' : 'Test Mail'}
          </button>
          <button type="submit" className={cls.saveBtn} disabled={loading || isSendingTest}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
